import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import styles from "./OrderDetail.module.css";
import Api from "../../utils/Api/Api";
import { money, dateTime, longDate } from "../../utils/format";
import { track, EVENTS } from "../../utils/analyticsService";
import { TopBar } from "../../components/TopBar/TopBar";
import { StatusChip, orderState } from "../../components/StatusChip/StatusChip";
import { LicenceKeyCard } from "../../components/LicenceKeyCard/LicenceKeyCard";
import { ErrorFallback } from "../../components/Error/Error";
import { GiftTag, GiftClaimedBanner, isReceivedGift, isSentGift } from "../../components/GiftTag/GiftTag";
import { copyText, whatsAppUrl } from "../../utils/gift";
import FleekIcon from "../../asset/icons/Brand_logo_Sq.png";

const paidVia = (payment = {}) => {
  const method = payment.payment_method || "";
  const via = payment.gateway || payment.bank_name || payment.card_brand || "";
  const last4 = payment.last_four_digits_card ? `···· ${payment.last_four_digits_card}` : "";
  return [method, via || last4].filter(Boolean).join(" · ") || "—";
};

/**
 * Order detail, rebuilt from design/Purchase Flow.dc.html (isOrderDetail block):
 * summary card with the order's facts, the licence key, help and invoice.
 */
export const OrderDetail = () => {
  const { order_id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const claimedGift = location.state?.giftClaimed || null;

  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let cancelled = false;
    Api.GetOrderDetails(order_id)
      .then(({ data }) => {
        if (cancelled) return;
        setOrder(data || null);
        if (data) {
          track(EVENTS.ORDER_VIEWED, {
            order_id: order_id,
            plan_id: data.subscription_plan?.subscription_plan_id ?? null,
            status: data.status || null,
            context: "order_detail",
          });
        }
      })
      .catch((err) => {
        console.error("Order detail failed to load:", err);
        if (!cancelled) setHasError(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [order_id]);

  const view = useMemo(() => {
    if (!order) return null;
    const plan = order.subscription_plan || {};
    const currency = order.details?.currency_type;
    const reference = order.representation_id || order.id || order_id;
    // The customer's activation deadline; the code's stock date only for older responses.
    const deadline = order.activate_by
      ? ["Activate by", longDate(order.activate_by)]
      : ["Valid till", order.coupon_code?.time_of_expiry ? longDate(order.coupon_code.time_of_expiry) : "—"];
    // A received gift was paid for by someone else: never show what it cost.
    const received = isReceivedGift(order);
    const meta = [
      ["Order ID", `#${reference}`],
      [received ? "Received on" : "Placed on", dateTime(received ? order.gift?.claimed_at || order.updated_at : order.updated_at || order.created_at) || "—"],
      ...(received
        ? []
        : [
            ["Paid via", paidVia(order.payment)],
            ["Amount paid", order.payment?.amount != null ? money(order.payment.amount, currency) : "—"],
          ]),
      deadline,
    ];
    return {
      reference,
      logo: plan.image_url || FleekIcon,
      brand: plan.service_name || order.product_name || "",
      plan: order.product_digital ? plan.plan_name : order.product_name,
      state: orderState(order),
      meta,
      replacementRequest: order.activation_status === "expired" ? order.replacement_request : null,
      gift: order.gift || null,
      sentGift: isSentGift(order),
      licenceKey: order.coupon_code?.coupon_code || null,
      activationUrl: order.coupon_code?.coupon_link || plan.code_redemption_url || null,
      isSample: !!order.is_mock,
    };
  }, [order, order_id]);

  if (hasError) return <ErrorFallback />;

  return (
    <div className={styles.page}>
      <TopBar title={view ? `Order #${view.reference}` : "Order"} backTo="/myorder" />
      <div className={styles.shell}>
        {loading || !view ? (
          <div className={styles.skeleton} />
        ) : (
          <>
            <GiftClaimedBanner claimed={claimedGift} />
            <section className={styles.card}>
              <div className={styles.head}>
                <img
                  className={styles.logo}
                  src={view.logo}
                  alt=""
                  onError={(e) => {
                    e.currentTarget.src = FleekIcon;
                  }}
                />
                <div className={styles.names}>
                  <div className={styles.brand}>{view.brand}</div>
                  {view.plan && <div className={styles.plan}>{view.plan}</div>}
                  {view.gift && (
                    <div className={styles.giftTag}>
                      <GiftTag gift={view.gift} />
                    </div>
                  )}
                </div>
                <StatusChip state={view.state} large />
              </div>

              <div className={styles.meta}>
                {view.meta.map(([label, value]) => (
                  <div className={styles.metaRow} key={label}>
                    <span className={styles.metaLabel}>{label}</span>
                    <span className={styles.metaValue}>{value}</span>
                  </div>
                ))}
              </div>
            </section>

            {view.sentGift && !view.licenceKey ? (
              // The code goes to whoever holds the gift, so the buyer has no
              // key here - and must not be told one is "activating".
              <>
                <p className={styles.giftNote}>
                  {view.gift?.state === "claimed"
                    ? `${view.gift?.claimed_by_name || "They"} claimed this gift. The code is theirs.`
                    : "You sent this as a gift. The code goes to whoever claims your link."}
                </p>
                {/* Still waiting to be claimed: the link can be sent (again) from here. */}
                {view.gift?.state === "ready" && view.gift?.link && (
                  <div className={styles.giftShare}>
                    <button
                      type="button"
                      className={styles.giftSend}
                      onClick={() => {
                        track(EVENTS.GIFT_SHARED, { via: "whatsapp", context: "order_detail" });
                        window.open(whatsAppUrl(view.gift.share_message || view.gift.link), "_blank", "noopener,noreferrer");
                      }}
                    >
                      Send on WhatsApp
                    </button>
                    <button
                      type="button"
                      className={styles.help}
                      onClick={async () => {
                        if (!(await copyText(view.gift.link))) return;
                        track(EVENTS.GIFT_SHARED, { via: "copy", context: "order_detail" });
                        setCopied(true);
                        setTimeout(() => setCopied(false), 2000);
                      }}
                    >
                      {copied ? "Link copied" : "Copy gift link"}
                    </button>
                  </div>
                )}
              </>
            ) : (
              <>
                <LicenceKeyCard
                  label="Licence key"
                  licenceKey={view.licenceKey}
                  activationUrl={view.activationUrl}
                  tracking={{ order_id: order_id, plan_id: order?.subscription_plan?.subscription_plan_id ?? null, brand: view.brand }}
                />
                {view.replacementRequest && (
                  // Past the activation window. The key above stays visible - many brands
                  // still accept it - and this asks support for a replacement, it doesn't promise one.
                  <section className={styles.replacement}>
                    <p className={styles.replacementText}>{view.replacementRequest.message}</p>
                    {view.replacementRequest.url && (
                      <a
                        className={styles.replacementCta}
                        href={view.replacementRequest.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() =>
                          track("replacement_request_opened", {
                            channel: view.replacementRequest.channel,
                            order_id: view.reference,
                          })
                        }
                      >
                        {view.replacementRequest.label || "Request a replacement"}
                      </a>
                    )}
                  </section>
                )}
              </>
            )}
            {view.isSample && (
              <p className={styles.sampleNote}>Sample key from the design — this is a mock order, not a real licence.</p>
            )}

            <div className={styles.actions}>
              <button type="button" className={styles.help} onClick={() => navigate("/support")}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 12a9 9 0 1 1-3.2-6.9" />
                  <path d="M12 17h.01" />
                  <path d="M12 13.5c0-1.5 2-1.8 2-3.3a2 2 0 1 0-4 0" />
                </svg>
                Need help with this order
              </button>
              {/* No invoice endpoint exists yet; present as designed, disabled. */}
              <button type="button" className={styles.invoice} disabled aria-disabled="true" title="Coming soon">
                Download invoice
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default OrderDetail;
